/**
 * The selected-video child channel and the combined statement it makes with
 * packet-copy audio.
 *
 * ===========================================================================
 * WHY THIS IS NOT A FIELD ON THE AUDIO POLICY
 *
 * Public mutation is unified at `GET|PUT /v1/rooms/{session_id}/recording`.
 * The controller still exposes audio and video child states because they
 * converge independently after the parent revision commits; this file derives
 * privacy-safe video presentation from that read-only child state.
 *
 * The consequence for this screen is the entire reason this file exists: the
 * old indicator expressed ONE channel. A participant reading "Audio capture
 * off" while their camera is being retained is being actively misled, and that
 * is precisely the failure the indicator exists to prevent.
 *
 * ===========================================================================
 * THE THREE THINGS VIDEO HAS THAT AUDIO DOES NOT
 *
 * 1. IT CAN BE STORING AND CHANGING AT THE SAME TIME. `VideoPolicyTransition`
 *    has a fifth member, `reconfiguring`, which audio's does not. Switching
 *    from `focused_guest` to `active_speaker` never stops storing video; only
 *    the SELECTION moves. Presenting that window as "starting" would be a lie
 *    in the harmless direction and presenting it as "stable" would be a lie in
 *    the harmful one, so it gets its own state.
 *
 * 2. IT HAS A SUBJECT. "Video is being kept" is not enough. `focused_guest`
 *    keeps ONE named participant and — quoting the published guide — "does NOT
 *    cut to the other speaker". A participant who is not that person is in a
 *    materially different situation from one who is, and cannot tell without
 *    being told whose camera it is. See `videoCameraInclusion`.
 *
 * 3. ITS `actual` IS A BARE MODE. `VideoRecordingPolicyStatus.actual` is a
 *    `VideoRecordingMode` with NO focus identity attached. So during a
 *    reconfigure, the outgoing program's subject is not nameable from the
 *    policy snapshot alone. `videoSubjects()` recovers it from `history` when
 *    the controller sent it and degrades conservatively when it did not.
 *
 * ===========================================================================
 * `actual` IS ASSUMED TO LIE, ON THIS CHANNEL TOO
 *
 * FINDINGS #42 recorded the audio channel's accept response reporting
 * `actual:"none"` and `active_capture_count:0` in the same body as
 * `transition:"disabling"` and `applied_revision:1 < revision:2`, while a live
 * microphone source was still closing. `VideoRecordingPolicyStatus.actual`
 * carries the same class of caveat in its own description — "none when no
 * recorder program may own media […] This does not by itself prove a first
 * video frame or a ready MP4" — and nothing upstream says its accept-time
 * snapshot is any more conservative than audio's was.
 *
 * So video's converging state is derived from BOTH `transition` AND
 * `applied_revision < revision`, never from `actual` alone, and
 * `active_program_count > 0` is treated exactly as `active_capture_count > 0`
 * is on the audio channel: a settled `none` with a non-zero count means a
 * recorder program may still own media, and "off" is the one claim this
 * indicator must never make when it is not true. This is an assumption of
 * symmetry with a measured audio defect, not a measurement of video — it stays
 * until video is measured and proven better.
 */

import type {
  RoomAudioRecordingPolicy,
  RoomVideoRecordingPolicy as SharedVideoPolicy,
  VideoRecordingMode,
  VideoRecordingSpec,
} from '@era/shared'
import {
  ARMED_AUDIO_CLAUSE,
  audioRetentionLevel,
  fenceConverging,
  pollIntervalFor,
  recordingPollInterval,
  recordingPresentation,
  SETTLED_POLL_MS,
  type RecordingPresentation,
  type RetentionLevel,
} from './recording-control'

/* -------------------------------------------------------------------------- */
/* the wire shapes                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The BFF↔browser policy shape, plus the one field it does not carry.
 *
 * `RoomVideoRecordingPolicy` in `packages/shared/src/contract.ts` is the
 * authority and is imported, not re-declared. The intersection below adds
 * `history` as OPTIONAL, and the reason is the whole "whose camera" problem:
 *
 *   `actual` is a bare MODE with no focus identity. So during a reconfigure —
 *   `focused_guest(A)` -> `focused_guest(B)` — the policy snapshot says the
 *   incoming subject is B and says nothing whatsoever about A, even though A's
 *   program may not have torn down yet. Upstream's
 *   `VideoRecordingPolicyStatus.history[]` is the ONLY place a past revision's
 *   `focus_identity` survives, and the app contract deliberately drops it.
 *
 * So this module accepts it when it is there and degrades conservatively when
 * it is not: `videoSubjects` returns `outgoing: 'unknown'`, which floors
 * `videoCameraInclusion` at `selectable` and stops a participant who WAS the
 * focus from being told they are clear. Declaring it optional rather than
 * demanding it keeps the BFF free to add it later without a change here — and
 * makes the degradation a tested path rather than a hypothetical one.
 *
 * The shared type is assignable to this one, so callers pass theirs unchanged.
 */

export type { VideoRecordingMode, VideoRecordingSpec }

/** One immutable revision of the desired selection. */
export interface VideoRecordingPolicyEvent {
  revision: number
  desired: VideoRecordingSpec
}

export type RoomVideoRecordingPolicy = SharedVideoPolicy & {
  history?: readonly VideoRecordingPolicyEvent[]
}

/* -------------------------------------------------------------------------- */
/* convergence                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Video's in-flight words. Three, because `reconfiguring` is one of them.
 *
 * Note what including it means: a reconfigure IS unsettled, so the compare-and-
 * set control locks and the poll speeds up, exactly as for an enable. What it
 * does NOT mean is that video has stopped being stored — `videoPresentation`
 * routes that case to `reconfiguring`, not to `starting`.
 */
const VIDEO_IN_FLIGHT: ReadonlySet<string> = new Set(['enabling', 'reconfiguring', 'disabling'])

export function videoConverging(policy: RoomVideoRecordingPolicy): boolean {
  return fenceConverging(policy, VIDEO_IN_FLIGHT)
}

/**
 * Six states: audio's five, plus the one only video can be in.
 *
 * `reconfiguring` means video is definitely being stored AND the subject is in
 * flux. Both halves matter, and neither of audio's states can carry them
 * together.
 */
export type VideoPresentation =
  | 'live'
  | 'starting'
  | 'reconfiguring'
  | 'stopping'
  | 'off'
  | 'failed'

export function videoPresentation(
  policy: RoomVideoRecordingPolicy,
  /**
   * Was video ALREADY being stored before this revision? From the parent
   * policy's `previous_desired`, which the child status does not carry.
   *
   * `undefined`/`null` means "not known", and the conservative inference below
   * is kept for that case — a build that cannot see the parent still behaves
   * exactly as it did.
   *
   * WHY THIS PARAMETER EXISTS. MEASURED 2026-08-10.
   *
   * On a `none -> video` enable the controller reports, in one body:
   *
   *     transition: enabling   actual: video   revision 2 / applied 1
   *     active_capture_count: 0   active_program_count: 1
   *
   * Every one of the three signals below fires on that body — `actual` is not
   * `none`, and a program already exists — so the inference concluded "a
   * program was already running" and returned `reconfiguring`, which this
   * module treats as DEFINITELY STORING. The pill then read "Audio + video
   * stored" while `active_capture_count` was 0 and nothing had been captured.
   *
   * The inference was not wrong about the field values; it was wrong about what
   * they mean on a FIRST enable, where `active_program_count: 1` is the program
   * that was just created, not one that predates the change. The platform team
   * put it exactly: "Program ownership is not proof that audio packets or video
   * frames were produced."
   *
   * `previous_desired` is the only signal that separates the two cases, which
   * is why it is threaded down here rather than inferred.
   */
  previouslyStoringVideo?: boolean | null,
): VideoPresentation {
  if (policy.transition === 'failed') return 'failed'

  if (videoConverging(policy)) {
    // A stop, whatever it is stopping from. `stopping` already means "it may
    // still be recording", so there is nothing more conservative to say.
    if (policy.desired.mode === 'none') return 'stopping'

    // A FIRST enable, known from the parent. Nothing was being stored a moment
    // ago, so nothing is being stored yet — regardless of what a freshly
    // created program's count says. `reconfiguring` here would promise that
    // video was already being kept, which is the one direction this state
    // cannot claim.
    if (previouslyStoringVideo === false) return 'starting'

    // Moving to a non-none mode with NO parent signal threaded — a rolling
    // upgrade or a cached legacy read. The parent is the documented source for
    // direction; this branch is the fallback for not having asked it.
    //
    // `active_program_count` USED TO BE A THIRD TERM HERE AND HAS BEEN REMOVED.
    // The platform team's clarification, 2026-08-10: it means "currently
    // nonterminal programs", explicitly "not a count of past programs or
    // evidence that video was previously enabled", and "may already be one
    // during the first none->video enable". Reading it as history is precisely
    // the bug that made the pill claim "Audio + video stored" over an
    // un-started capture. It is still read elsewhere in this file, but only
    // ever as CURRENT work — "a program may still own media, so do not say
    // off" — which is what it actually means.
    //
    // What is left is a transition the controller itself calls a reconfigure,
    // and an `actual` that names a mode. `actual` is not trusted alone
    // (header), but it is trusted in the direction that says something IS
    // running: a false positive costs a stronger warning, a false negative
    // costs a silent camera.
    if (policy.transition === 'reconfiguring' || policy.actual !== 'none') {
      return 'reconfiguring'
    }
    return 'starting'
  }

  if (policy.desired.mode !== 'none') {
    // ======================================================================
    // THERE IS NO `armed` HERE, AND THAT IS NOT AN OVERSIGHT.
    //
    // The audio channel grew one (`recording-control.ts`, `fenceArmed`): a
    // settled `desired:audio, actual:none, active:0` is a policy at rest
    // waiting for a microphone, measured, and it lasts the whole call in a
    // room where nobody unmutes. The identical FIELD SHAPE on this channel does
    // not mean the identical thing, and the comment that used to sit here —
    // "the ordinary state of a room that asked for video before anybody turned
    // a camera on" — was simply wrong. Three facts from the platform team,
    // 2026-08-09, and each one alone is enough:
    //
    //  1. `actual` IS RECORDER OWNERSHIP, NOT FRAMES. `has_video_content` is
    //     the frames claim and the two must never be merged. Ownership is
    //     granted by reconciling a non-`none` desired; it does not wait for
    //     anybody's camera. So "asked for video, nobody has a camera on" shows
    //     up as `actual != none` — as `live` — with the frames question
    //     answered by a field this contract does not even carry. It never
    //     lands in this branch.
    //  2. A mid-call enable that cannot get a recorder ends as
    //     `transition: failed`, not as a quiet resting state.
    //  3. At room creation with no recorder free, creation fails ATOMICALLY
    //     with a retryable 503 `no_video_recorder_capacity` — so a room cannot
    //     even come into being holding an unbacked video policy.
    //
    // What is left in this branch is therefore NOT a policy at rest: it is a
    // settled request for video that no recorder program is reported to own.
    // Whatever that is, it is not "nothing is happening and nothing will",
    // which is the entire content of `armed`. `starting` keeps it at
    // `unsettled` — "assume it is being stored" — which is the conservative
    // side of a state nobody has explained yet. FINDINGS asks upstream whether
    // it is reachable at all.
    return policy.actual !== 'none' ? 'live' : 'starting'
  }

  // Desired `none`, settled. `active_program_count` is the tell: a program that
  // may own media has not finished letting go of it.
  if (policy.actual !== 'none' || policy.active_program_count > 0) return 'stopping'
  return 'off'
}

/** Video's contribution to the combined statement. See `audioRetentionLevel`. */
export function videoRetentionLevel(presentation: VideoPresentation): RetentionLevel {
  switch (presentation) {
    case 'live':
    // Storing throughout. The change is to WHOSE camera, not to whether.
    case 'reconfiguring':
      return 'on'
    case 'starting':
    case 'stopping':
    case 'failed':
      return 'unsettled'
    case 'off':
      return 'off'
  }
}

export function videoPollInterval(
  policy: RoomVideoRecordingPolicy | null | undefined,
  divergedForMs: number,
): number {
  if (!policy) return SETTLED_POLL_MS
  // Diverged compares MODES, because `actual` has nothing else to compare. A
  // focus-only change (focused_guest A -> focused_guest B) leaves the modes
  // equal and is caught by the revision fence inside `videoConverging`.
  return pollIntervalFor(
    videoConverging(policy),
    policy.desired.mode !== policy.actual,
    divergedForMs,
  )
}

/* -------------------------------------------------------------------------- */
/* whose camera                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Every selection that may own media right now.
 *
 * `incoming` is what the room is moving to; `outgoing` is a selection that has
 * not finished letting go. Both can be live at once during a reconfigure, and
 * during that window a participant is exposed if EITHER selects them — which is
 * why this returns a pair rather than one spec.
 *
 * `outgoing: 'unknown'` is the honest answer to a real gap: `actual` is a bare
 * mode with no focus identity, so when the controller did not send `history`
 * this client cannot name the selection that is winding down. It is not
 * cosmetic — it is what stops `videoCameraInclusion` from telling a
 * previously-focused participant they are no longer being kept.
 */
export interface VideoSubjects {
  incoming: VideoRecordingSpec | null
  outgoing: VideoRecordingSpec | 'unknown' | null
}

export function videoSubjects(policy: RoomVideoRecordingPolicy): VideoSubjects {
  const presentation = videoPresentation(policy)
  if (presentation === 'off') return { incoming: null, outgoing: null }

  const incoming = policy.desired.mode === 'none' ? null : policy.desired

  // Is there POSITIVE evidence that some program may own media right now?
  //
  // Not "is the policy unsettled": an enable that starts from nothing is
  // unsettled and has no prior program, and warning about a phantom one there
  // would train people to ignore the warning that matters. Each disjunct below
  // is a distinct piece of evidence, and `transition` is included because it is
  // the signal that outlived `actual` and the count on the audio channel
  // (FINDINGS #42) — a stop whose accept response already zeroed both still
  // said `disabling`.
  const mayOwnMedia =
    policy.active_program_count > 0 ||
    policy.actual !== 'none' ||
    policy.transition === 'reconfiguring' ||
    policy.transition === 'disabling'

  let outgoing: VideoRecordingSpec | 'unknown' | null = null
  if (mayOwnMedia) {
    const applied = lastAppliedSelection(policy)
    if (!applied) {
      // No usable history, and `actual` cannot name a subject. If the running
      // program is demonstrably the selection we already have in `incoming`,
      // there is nothing extra to say; otherwise it is genuinely unknown.
      const sameSelection =
        policy.actual === policy.desired.mode && policy.applied_revision === policy.revision
      outgoing = sameSelection ? null : 'unknown'
    } else if (!sameSpec(applied, incoming)) {
      outgoing = applied
    }
  }

  // The invariant that ties this back to the headline: if the readout is going
  // to say video is being stored, this function has to be able to say by whom.
  // A settled `none` with a leaked program and no history lands here — nothing
  // above could name it, but "no video program at all" would contradict the
  // pill directly.
  if (!incoming && !outgoing) return { incoming, outgoing: 'unknown' }
  return { incoming, outgoing }
}

/**
 * The most recent selection the recorder actually acted on that was not `none`.
 *
 * Not simply the entry AT `applied_revision`: when the applied revision is the
 * stop itself, the program that may still be running belongs to the last
 * non-`none` window before it. Searching backwards from `applied_revision` is
 * what makes a leaked program nameable instead of anonymous.
 */
function lastAppliedSelection(policy: RoomVideoRecordingPolicy): VideoRecordingSpec | null {
  if (!policy.history?.length) return null
  let best: VideoRecordingPolicyEvent | null = null
  for (const entry of policy.history) {
    if (entry.revision > policy.applied_revision) continue
    if (entry.desired.mode === 'none') continue
    if (!best || entry.revision > best.revision) best = entry
  }
  return best?.desired ?? null
}

function sameSpec(a: VideoRecordingSpec, b: VideoRecordingSpec | null): boolean {
  if (!b) return false
  return (
    a.mode === b.mode &&
    (a.focus_identity ?? null) === (b.focus_identity ?? null) &&
    a.screen_share === b.screen_share
  )
}

/**
 * Whether THIS participant's camera is among what is kept.
 *
 * Four answers, and the asymmetry between them is the point:
 *
 *  - `kept`         — this selection keeps you whenever it is storing.
 *  - `selectable`   — the recorder may cut to you at any moment.
 *  - `not-selected` — this selection never chooses your camera.
 *  - `none`         — no video program at all.
 *
 * `not-selected` is the ONLY negative claim in this module, and it is made in
 * exactly one situation: a `focused_guest` selection naming somebody else, with
 * no unnameable prior program still running. That is the one place upstream
 * gives a guarantee strong enough to support it — the guide is explicit that
 * the recorder "keeps only that participant's selected encoded camera; it does
 * NOT cut to the other speaker". Every other mode can reach anyone.
 *
 * It is about the CAMERA SELECTION and nothing else. A screen share is stored
 * in EVERY mode (`screen_share: 'replace'` — "In every mode a screen share
 * replaces the camera until sharing stops"), and whether a NON-selected
 * participant's share is the one that replaces it is not stated anywhere
 * upstream. Rather than guess, this function does not model screen share at
 * all and `SCREEN_SHARE_NOTE` is stated unconditionally alongside it — so the
 * warning does not depend on a flag the caller might forget to pass.
 */
export type ViewerCameraInclusion = 'none' | 'kept' | 'selectable' | 'not-selected'

const INCLUSION_RANK: Record<ViewerCameraInclusion, number> = {
  none: -1,
  'not-selected': 0,
  selectable: 1,
  kept: 2,
}

export function videoCameraInclusion(
  policy: RoomVideoRecordingPolicy,
  viewerIdentity: string | null | undefined,
): ViewerCameraInclusion {
  const { incoming, outgoing } = videoSubjects(policy)
  if (!incoming && !outgoing) return 'none'

  const candidates: ViewerCameraInclusion[] = []
  if (incoming) candidates.push(inclusionForSpec(incoming, viewerIdentity))
  // An unnameable prior program floors the answer at `selectable`: it may be
  // focused on anyone, including this viewer, and saying otherwise would be
  // the exact lie this module exists to prevent.
  if (outgoing === 'unknown') candidates.push('selectable')
  else if (outgoing) candidates.push(inclusionForSpec(outgoing, viewerIdentity))

  // Most inclusive wins. During a reconfigure a participant is exposed if
  // EITHER selection reaches them, so the union is the truthful answer.
  return candidates.reduce((best, next) =>
    INCLUSION_RANK[next] > INCLUSION_RANK[best] ? next : best,
  )
}

function inclusionForSpec(
  spec: VideoRecordingSpec,
  viewerIdentity: string | null | undefined,
): ViewerCameraInclusion {
  const isFocus = Boolean(viewerIdentity) && spec.focus_identity === viewerIdentity
  switch (spec.mode) {
    case 'none':
      return 'not-selected'
    case 'focused_guest':
      // The one guaranteed negative. Without a viewer identity we cannot make
      // it, so an anonymous caller gets `selectable` rather than a false all-
      // clear.
      if (!viewerIdentity) return 'selectable'
      return isFocus ? 'kept' : 'not-selected'
    case 'active_speaker':
      return 'selectable'
    case 'auto':
      // Headcount-dependent by definition: "a fixed focus in 1:1 and active
      // speaker at three or more humans". The policy snapshot carries no
      // headcount, so this client cannot tell which branch is live. The named
      // focus is `kept` under the 1:1 branch and `selectable` under the other,
      // so `kept` is the union; everyone else is `selectable` either way.
      return isFocus ? 'kept' : 'selectable'
    case 'meeting_view':
      // "The explicit two-camera side-by-side choice." Both humans are kept.
      // Upstream only defines it for a two-human call and says nothing about
      // what happens with a third, and this client cannot count humans — so
      // `kept` is returned for everyone, which is the conservative side of that
      // gap rather than the convenient one.
      return 'kept'
  }
}

/* -------------------------------------------------------------------------- */
/* words                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Stated unconditionally whenever any video program may be running.
 *
 * Not guessable from the field name, and it is the single most surprising
 * retention fact in the feature: a participant who is NOT the focus can still
 * put their screen into the recording by sharing it. CONFIRMED by the platform
 * team 2026-08-09 — "screen-share selection happens before camera mode/focus
 * selection and considers shares from every non-hidden participant. Therefore,
 * a non-selected participant's screen share can be retained."
 *
 * WORDED DELIBERATELY LOOSELY, and the earlier version was wrong.
 *
 * It used to read "A screen share replaces the camera in every mode, so a
 * shared screen is stored for as long as sharing lasts." The platform team
 * called that TOO ABSOLUTE, and they are right on both halves:
 *
 *  - "replaces" is not guaranteed. Only ONE share is retained at a time. When
 *    several people share, the most recently published one wins, and if that
 *    one disappears while others remain the recorder currently falls back by
 *    Track SID. That fallback is an INTERNAL IMPLEMENTATION DETAIL, is not
 *    exposed by the lightweight policy API, and must not be implied here — a
 *    client that describes it would be documenting something it cannot observe
 *    and that is free to change.
 *  - "for as long as sharing lasts" promises a duration this app cannot see.
 *
 * So the wording below is the platform team's own safe phrasing, kept verbatim.
 * It says what a participant needs to decide whether to share, and stops
 * exactly where the observable contract stops. Do not "improve" it into
 * something more specific without a field that makes the specifics readable.
 */
export const SCREEN_SHARE_NOTE =
  'Any participant’s screen share may replace the retained camera; when several people share, one share is retained.'

/**
 * Storage is not transport. Both channels need this said, and it is said in the
 * panel rather than in the announced sentence so it is not re-read aloud on
 * every transition.
 */
export const TRANSPORT_NOTE =
  'This is about storage only. Nobody is muted, no camera is switched off, and everyone in the room can still see and hear you either way.'

/**
 * Whose camera one selection keeps, in full, as a clause that fits after
 * "Video keeps".
 *
 * The `focused_guest` clause carries the no-cutting guarantee inline because
 * that guarantee IS the difference between it and `active_speaker`, and a
 * participant who is not the focus has no way to know it otherwise.
 */
export function videoSelectionPhrase(
  spec: VideoRecordingSpec,
  resolve?: ResolveParticipantName,
): string {
  switch (spec.mode) {
    case 'none':
      return 'nothing'
    case 'focused_guest':
      return `only ${nameOf(spec, resolve)}, and it does not cut to whoever is speaking`
    case 'active_speaker':
      return 'whoever is speaking, one person at a time'
    case 'auto':
      return spec.focus_identity
        ? `${nameOf(spec, resolve)} in a two-person call, or whoever is speaking once there are three or more`
        : 'whoever is speaking, and one fixed camera in a two-person call'
    case 'meeting_view':
      return 'both cameras, side by side'
  }
}

/**
 * The same selection in a form that survives being embedded in a larger
 * sentence.
 *
 * Two forms rather than one because the long clauses do not compose: "switching
 * from only Dana's camera, and it does not cut to whoever is speaking to only
 * Sam's camera, and it does not cut to whoever is speaking" is unreadable, and
 * an unreadable privacy statement is a privacy statement nobody reads. The
 * guarantees the short form drops are restated by `videoSubjectSentence` in
 * their own sentence instead of being lost.
 */
export function videoSelectionShortPhrase(
  spec: VideoRecordingSpec,
  resolve?: ResolveParticipantName,
): string {
  switch (spec.mode) {
    case 'none':
      return 'nothing'
    case 'focused_guest':
      return `only ${nameOf(spec, resolve)}`
    case 'active_speaker':
      return 'whoever is speaking'
    case 'auto':
      return spec.focus_identity
        ? `${nameOf(spec, resolve)}, or whoever is speaking`
        : 'whoever is speaking'
    case 'meeting_view':
      return 'both cameras'
  }
}

/**
 * A participant identity resolved to the name humans use for them.
 *
 * Returns null when the identity is not in the roster — someone who has
 * already left, or a selection made before this browser joined.
 */
export type ResolveParticipantName = (identity: string) => string | null

/**
 * WHY THIS TAKES A RESOLVER INSTEAD OF PRINTING `focus_identity`.
 *
 * `focus_identity` is a `u_` identity: opaque, non-PII, minted by the BFF and
 * deliberately unrelated to anybody's name (see packages/shared/src/ids.ts —
 * a display name can never be an identity, because it lands in an object key
 * that cannot be rewritten on an erasure request). Printing it produced
 * sentences like "Video keeps only u_zVjCXnjP4J's camera", which is the app
 * showing a participant its own internal plumbing at the exact moment it is
 * making a privacy claim they need to understand. FINDINGS #50.
 *
 * The roster lives in the call, not here — this module is pure policy logic
 * and imports no LiveKit — so the name arrives as a function. When it cannot
 * resolve, the fallback says "one named person", which is true, unambiguous
 * about the fact that ONE camera is kept, and does not pretend to know who.
 */
function nameOf(spec: VideoRecordingSpec, resolve?: ResolveParticipantName): string {
  if (!spec.focus_identity) return 'one camera the room chose'
  const name = resolve?.(spec.focus_identity)
  return name ? `${name}'s camera` : "one named person's camera"
}

/** The full "whose camera" sentence, including any selection still winding down. */
export function videoSubjectSentence(
  policy: RoomVideoRecordingPolicy,
  resolve?: ResolveParticipantName,
): string | null {
  const { incoming, outgoing } = videoSubjects(policy)
  if (!incoming && !outgoing) return null

  // Split on the unnameable case first, so the two nameable branches below are
  // reading a real spec rather than a cast.
  if (outgoing === 'unknown') {
    return incoming
      ? `Video keeps ${videoSelectionPhrase(incoming, resolve)}. A previous selection may still be running and this build cannot name it, so treat any camera as kept until the change lands.`
      : 'This build cannot name the selection that is still running, so treat any camera as kept until it stops.'
  }
  if (incoming && outgoing) {
    // Both selections are named, and the "either" is not hedging — during a
    // reconfigure a participant is exposed if EITHER of them reaches them.
    const from = videoSelectionShortPhrase(outgoing, resolve)
    const to = videoSelectionShortPhrase(incoming, resolve)

    /*
     * Two DIFFERENT people whose names we cannot resolve produce the same
     * phrase, and "either only one named person's camera or only one named
     * person's camera" is worse than useless — it reads like a rendering bug
     * and tells a participant nothing.
     *
     * So collapse it, and keep the fact that matters: two cameras are in play
     * and either may be kept. This is only reachable when the roster cannot
     * name them (they left, or the change predates this browser joining); when
     * it can, the two phrases differ and the normal sentence stands.
     */
    if (from === to) {
      const distinct =
        outgoing.focus_identity && incoming.focus_identity
          ? outgoing.focus_identity !== incoming.focus_identity
          : false
      return distinct
        ? 'Until the change lands, either of two named people’s cameras may be kept.'
        : `Until the change lands, ${to} may be kept.`
    }
    return `Until the change lands, either ${from} or ${to} may be kept.`
  }
  if (incoming) return `Video keeps ${videoSelectionPhrase(incoming, resolve)}.`
  // Only reachable with a nameable outgoing selection and no incoming one: a
  // stop that has not finished letting go.
  if (!outgoing) return null
  return `Until it stops, video may still be keeping ${videoSelectionPhrase(outgoing, resolve)}.`
}

/** The personal answer, which is the one a participant actually needs. */
export function videoViewerSentence(inclusion: ViewerCameraInclusion): string | null {
  switch (inclusion) {
    case 'none':
      return null
    case 'kept':
      return 'Your camera is one of the cameras being kept.'
    case 'selectable':
      return 'Your camera can be the one kept at any moment.'
    case 'not-selected':
      return 'Your camera is not the one being kept — but a screen share you start is stored in its place.'
  }
}

/** Two or three words for the pill, when video may be stored. */
export function viewerCameraChip(inclusion: ViewerCameraInclusion): string | null {
  switch (inclusion) {
    case 'none':
      return null
    case 'kept':
      return 'You'
    case 'selectable':
      return 'Anyone'
    case 'not-selected':
      return 'Not you'
  }
}

export function videoModeLabel(mode: VideoRecordingMode): string {
  switch (mode) {
    case 'none':
      return 'Off'
    case 'auto':
      return 'Automatic'
    case 'focused_guest':
      return 'One guest'
    case 'active_speaker':
      return 'Active speaker'
    case 'meeting_view':
      return 'Side by side'
  }
}

/* -------------------------------------------------------------------------- */
/* the combined, two-channel statement                                         */
/* -------------------------------------------------------------------------- */

/** What a participant must ASSUME is being stored right now. */
export type StoredChannels = 'nothing' | 'audio' | 'video' | 'both'

export interface RetentionReadout {
  audio: RetentionLevel
  video: RetentionLevel
  audioPresentation: RecordingPresentation
  /** null only when this build cannot observe the video channel at all. */
  videoPresentation: VideoPresentation | null
  /**
   * The conservative reading, and the field the UI is built on. `unsettled`
   * counts as stored: a person deciding whether to say something out loud is
   * harmed by a false "no" and merely inconvenienced by a cautious "maybe".
   */
  assume: StoredChannels
  /** Either channel is mid-flight. The pill must not read as settled. */
  settling: boolean
  /** Either channel is in a TERMINAL unresolved state. */
  unresolved: boolean
  /** The video channel is not observable by this build. Never means "off". */
  videoUnknown: boolean
  /** Whether this viewer's own camera is among what is kept. */
  viewerCamera: ViewerCameraInclusion
  /** The pill's words. Complete without colour, per WCAG 1.4.1. */
  label: string
  /** The unambiguous statement: the live region and the panel headline. */
  sentence: string
  /**
   * `armed` is its own tone and never `idle`.
   *
   * `idle` is the all-clear: nothing is stored and nothing will be until
   * somebody changes the policy. Armed shares the first half and not the
   * second, and the pill's icon and colour are the fastest-read part of the
   * screen — a green shield over a policy that starts storing on its own is
   * the exact misread this indicator exists to prevent.
   */
  tone: 'idle' | 'armed' | 'recording' | 'alert'
}

export interface RetentionInputs {
  audio: RoomAudioRecordingPolicy
  /**
   * `undefined` and `null` both mean NOT REPORTED, and neither means "off".
   *
   * A room with no retained video still has a policy whose mode is `none`; the
   * absence of the object means this build did not fetch one. Folding that
   * into "off" would print "Nothing stored" over a call that may be keeping
   * everyone's camera, which is the exact failure this file exists to prevent.
   * It is surfaced as its own state instead, and it disappears the moment the
   * BFF carries the policy through.
   */
  video?: RoomVideoRecordingPolicy | null
  /** This browser's participant identity, for the "is it me" answer. */
  viewerIdentity?: string | null
  /**
   * Resolves a `u_` identity to a human name for the retention sentence.
   *
   * Optional so every existing caller keeps compiling, but the participant-
   * facing surfaces MUST pass it: without it the sentence degrades to "one
   * named person's camera", which is honest but nameless. It never falls back
   * to the raw identity — see `nameOf` and FINDINGS #50.
   */
  resolveName?: ResolveParticipantName
  /**
   * The PARENT policy's `previous_desired`, which the child video status does
   * not carry. Only its mode is read, and only to answer one question: was
   * video already being stored before this revision?
   *
   * Optional so a caller that has not threaded it through keeps the previous
   * conservative behaviour rather than silently getting a weaker claim.
   */
  previousDesired?: { mode: string } | null
}

export function retentionReadout({
  audio,
  video,
  viewerIdentity,
  previousDesired,
  resolveName,
}: RetentionInputs): RetentionReadout {
  const audioPresentation = recordingPresentation(audio)
  const audioLevel = audioRetentionLevel(audioPresentation)

  // `undefined` stays undefined — "the caller did not tell us" is a different
  // answer from "video was not being stored", and only the latter may weaken
  // the claim. `null` is the controller saying there IS no previous revision,
  // which means this is the first, which means video was not being stored.
  const previouslyStoringVideo =
    previousDesired === undefined ? undefined : previousDesired?.mode === 'video'

  const videoPresentation_ = video ? videoPresentation(video, previouslyStoringVideo) : null
  const videoLevel: RetentionLevel = videoPresentation_
    ? videoRetentionLevel(videoPresentation_)
    : 'unknown'
  const videoUnknown = videoLevel === 'unknown'
  const viewerCamera = video ? videoCameraInclusion(video, viewerIdentity) : 'none'

  // Armed is deliberately NOT `audioStored`. It is the truthful answer to "is
  // my voice being stored right now", and that answer is no. Everything that
  // stops it from being an all-clear is carried by the label, the headline and
  // the row — as WORDS about when it changes, which is the question armed
  // actually answers — rather than by inflating the four-way stored/not-stored
  // reading, which would make "Audio stored" mean two different things.
  const audioArmed_ = audioLevel === 'armed'
  const audioStored = audioLevel === 'on' || audioLevel === 'unsettled'
  const videoStored = videoLevel === 'on' || videoLevel === 'unsettled'
  const assume: StoredChannels = audioStored
    ? videoStored
      ? 'both'
      : 'audio'
    : videoStored
      ? 'video'
      : 'nothing'

  // Motion, not certainty — and they are NOT the same question. A reconfigure
  // is `on` (video is definitely being stored) and simultaneously in flight
  // (the subject is moving), so deriving this from the levels alone would have
  // shown the one state where the selection is changing as a settled one.
  const settling =
    audioLevel === 'unsettled' || videoLevel === 'unsettled' || (video ? videoConverging(video) : false)
  const unresolved =
    audioPresentation === 'failed' || videoPresentation_ === 'failed' || videoUnknown

  return {
    audio: audioLevel,
    video: videoLevel,
    audioPresentation,
    videoPresentation: videoPresentation_,
    assume,
    settling,
    unresolved,
    videoUnknown,
    viewerCamera,
    // `videoLevel === 'unsettled'` is the "assumed, not observed" test. It is
    // deliberately NOT `settling`: settling is also true during a reconfigure,
    // where video IS definitely being stored and only the subject is moving —
    // qualifying the label there would weaken a claim that is genuinely true.
    label: retentionLabel(
      assume,
      audioStored,
      videoUnknown,
      audioArmed_,
      videoLevel === 'unsettled',
      // WHICH WAY it is moving. Without this the pill said "video starting"
      // during a disable — a policy that had already been accepted and whose
      // recorder was winding down was announced as though it were about to
      // begin. It still correctly refuses to say "nothing stored" (the program
      // is genuinely still running), so this is a wording defect rather than a
      // denial, but a participant reading "starting" while their camera stops
      // being kept has been told the opposite of what is happening.
      videoPresentation_,
    ),
    sentence: retentionSentence({
      assume,
      audioPresentation,
      videoPresentation: videoPresentation_,
      video,
      videoUnknown,
      viewerCamera,
      audioArmed: audioArmed_,
      resolveName,
    }),
    tone: unresolved
      ? 'alert'
      : assume !== 'nothing'
        ? 'recording'
        : audioArmed_
          ? 'armed'
          : 'idle',
  }
}

/**
 * The four-way answer, as the FIRST words of the pill.
 *
 * The differentiating token leads — "Nothing" / "Audio" / "Video" / "Audio +
 * video" — because that is the token a glance catches. Nothing here depends on
 * colour or on an icon: the text alone answers the question, which is both the
 * accessibility requirement and the reason it survives a screenshot.
 *
 * `settling` is deliberately NOT in the label. Folding a transition into the
 * stored side is the whole conservative rule, and a label that said "stopping"
 * would be read as "stopped" — the mistake the audio indicator already had to
 * work around with "Still recording". The dot and the sentence carry the flux.
 *
 * ARMED IS in the label, and it is the one condition that earns a qualifier.
 * "Nothing stored" over an armed policy is the technically-true lie: right at
 * the instant it is read it is correct, and it stops being correct with no
 * click, no prompt and no second UI event. So the armed strings all carry the
 * trigger — "once a mic is on" — and they are built so that TRUNCATION IS SAFE:
 * every prefix of "Audio stored once a mic is on" reads as a stronger claim
 * than the whole, never a weaker one. The pill truncates on a narrow phone, and
 * a truncation that erased the qualifier would erase the wrong half.
 */
function retentionLabel(
  assume: StoredChannels,
  audioStored: boolean,
  videoUnknown: boolean,
  audioArmed: boolean,
  /**
   * True when video reaches `assume` as an ASSUMPTION rather than an
   * observation — `unsettled`, not `on`.
   *
   * MEASURED VIOLATION, 2026-08-09. An automated scan of 132 paired policy/UI
   * samples during a real call found exactly one overclaim, and it was here:
   * on a mid-call enable the controller reported `transition: enabling`,
   * `actual: none`, `active_program_count: 0` — nothing was being kept — while
   * this pill said "Audio + video stored". The control beside it read "no video
   * is being kept yet" and the panel row read "Starting — assume it is being
   * stored". Both hedged. Only the pill asserted.
   *
   * The cause is this module's own conservative rule turning on itself:
   * `videoRetentionLevel` maps `starting` to the stored level, which is right
   * for the DECISION (resolve ambiguity toward stored), and the pill then
   * rendered that decision as a STATEMENT OF FACT. Erring toward "stored" is
   * the safe direction for privacy, so this is not dangerous — but a pill that
   * contradicts the two components next to it teaches people the pill can be
   * wrong, which is worse than the wording it saved.
   *
   * So the assumption keeps its weight and loses its certainty. Note this
   * cannot be derived from `assume` alone: `assume: 'both'` is produced by both
   * "video is genuinely running" and "video might be", and only the level
   * distinguishes them.
   */
  videoAssumed: boolean,
  /** The video channel's own presentation, for the DIRECTION of an unsettled change. */
  videoPresentation_: VideoPresentation | null,
): string {
  // 'stopping' is the only unsettled state that is winding DOWN. Everything
  // else that reaches `videoAssumed` — starting, reconfiguring — is a change
  // that ends with video being kept.
  const winding = videoPresentation_ === 'stopping'
  const moving = winding ? 'stopping' : 'starting'
  // A build that cannot see the video channel must say so in the pill. Printing
  // "Nothing stored" here would be the exact lie the feature exists to prevent,
  // and printing "Video stored" would be an unearned alarm; so the pill reports
  // the defect, and the panel still states the audio channel plainly.
  if (videoUnknown) {
    if (audioStored) return 'Audio stored · video unknown'
    if (audioArmed) return 'Audio once a mic is on · video unknown'
    return 'Video retention unknown'
  }
  switch (assume) {
    case 'nothing':
      return audioArmed ? 'Audio stored once a mic is on' : 'Nothing stored'
    case 'audio':
      return 'Audio stored'
    case 'video':
      // Video is genuinely being stored AND audio is armed. Both facts, video
      // first because it is the one that is true right now.
      if (videoAssumed) {
        return audioArmed
          ? `Video ${moving} · audio once a mic is on`
          : `Video ${moving}`
      }
      return audioArmed ? 'Video stored · audio once a mic is on' : 'Video stored'
    case 'both':
      // Truncation stays safe, the same rule the armed strings follow: every
      // prefix of "Audio stored · video starting" names a real storage channel
      // before it reaches the qualifier, so a clipped pill under-promises the
      // uncertainty and never under-promises the storage.
      return videoAssumed ? `Audio stored · video ${moving}` : 'Audio + video stored'
  }
}

function retentionSentence(input: {
  assume: StoredChannels
  audioPresentation: RecordingPresentation
  videoPresentation: VideoPresentation | null
  video: RoomVideoRecordingPolicy | null | undefined
  videoUnknown: boolean
  viewerCamera: ViewerCameraInclusion
  audioArmed: boolean
  resolveName?: ResolveParticipantName
}): string {
  const parts: string[] = []

  if (input.videoUnknown) {
    parts.push(
      input.audioArmed
        ? ARMED_AUDIO_CLAUSE
        : input.assume === 'nothing'
          ? 'Audio is not being stored.'
          : 'Audio is being stored.',
      audioQualifier(input.audioPresentation) ?? '',
      'Whether video is being stored is not reported by this build — do not read that as “no video is kept”.',
    )
    return parts.filter(Boolean).join(' ')
  }

  // Either channel still converging means the headline states the safe
  // conclusion as guidance rather than asserting a fact that is not true yet.
  // `armed` is deliberately NOT unsettled: it is a settled policy at rest, and
  // it already has its own clause that carries the trigger.
  const eitherUnsettled =
    audioRetentionLevel(input.audioPresentation) === 'unsettled' ||
    (input.videoPresentation ? videoRetentionLevel(input.videoPresentation) === 'unsettled' : false)

  parts.push(headline(input.assume, input.audioArmed, eitherUnsettled))
  const guided = eitherUnsettled && input.assume === 'both'
  const audioNote = audioQualifier(input.audioPresentation, guided)
  if (audioNote) parts.push(audioNote)
  const videoNote = input.videoPresentation
    ? videoQualifier(input.videoPresentation, guided)
    : null
  if (videoNote) parts.push(videoNote)

  if (input.video && input.videoPresentation && input.videoPresentation !== 'off') {
    const subject = videoSubjectSentence(input.video, input.resolveName)
    if (subject) parts.push(subject)
    const personal = videoViewerSentence(input.viewerCamera)
    if (personal) parts.push(personal)
    if (input.video.desired.screen_share === 'replace') parts.push(SCREEN_SHARE_NOTE)
  }

  return parts.join(' ')
}

/** The video row's status words. See `audioChannelStatus` for the rule. */
export function videoChannelStatus(presentation: VideoPresentation | null): string {
  switch (presentation) {
    case 'live':
      return 'Being stored'
    case 'reconfiguring':
      // Never "changing" alone: the change is not a pause.
      return 'Being stored — the selection is changing'
    case 'starting':
      return 'Starting — assume it is being stored'
    case 'stopping':
      return 'Stopping — it may still be being stored'
    case 'failed':
      return 'Unresolved — assume it is being stored'
    case 'off':
      return 'Not stored'
    default:
      // Not "not stored". This build cannot see the channel.
      return 'Not reported by this build'
  }
}

/**
 * The headline states BOTH channels, always, including the one that is off.
 *
 * "Audio is being stored" on its own leaves video unstated, and an unstated
 * channel is read as absent. Naming both is what makes the four outcomes
 * distinguishable at the level of the sentence as well as the label.
 */
export function retentionHeadline(
  assume: StoredChannels,
  audioArmed = false,
  /**
   * Is any channel reaching `assume` as an ASSUMPTION rather than an
   * observation? Same discriminator the pill uses.
   *
   * When true the `both` case states the SAFE CONCLUSION as guidance instead of
   * asserting a fact, and the reason is that the fact is not true yet:
   *
   *   "Audio and video are both being stored"  is FALSE at the instant a
   *     `none -> video` enable is accepted — measured 2026-08-10 with
   *     `active_capture_count: 0` and nothing captured.
   *   "Treat this call as storing audio and video"  is TRUE at that instant,
   *     AND stays true a second later when it becomes literally true.
   *
   * This sentence is what the `aria-live` region announces and what the panel
   * headline shows, so a participant who never sees the pill gets only this. It
   * has to lead with the safe conclusion rather than make them assemble it from
   * qualifiers — which is why the fix is not simply mirroring the pill's
   * per-channel wording.
   *
   * "Treat this as…" is also the vocabulary the qualifiers already use, so the
   * lead stops contradicting its own tail.
   */
  unsettled = false,
): string {
  return headline(assume, audioArmed, unsettled)
}

function headline(assume: StoredChannels, audioArmed = false, unsettled = false): string {
  switch (assume) {
    case 'nothing':
      // Armed replaces this outright rather than qualifying it. "Nothing from
      // this call is being stored" as a headline over an armed policy is the
      // single most misleading sentence this screen could print: it is true for
      // as long as it takes to read and it is not an all-clear.
      if (audioArmed) return ARMED_AUDIO_CLAUSE
      // The one case where "storage" is most likely to be misheard as
      // "connection", so the transport fact is said in the same breath.
      return 'Nothing from this call is being stored. Everyone can still see and hear you.'
    case 'audio':
      return 'Audio is being stored. Video is not.'
    case 'video':
      // "Audio is not" would be the technically-true lie again, so the armed
      // room gets the trigger instead of the flat negative.
      return audioArmed
        ? 'Video is being stored. Audio is not being captured yet — it starts by itself the moment a microphone is on.'
        : 'Video is being stored. Audio is not.'
    case 'both':
      // Guidance while either channel is still converging, assertion once both
      // are observed. The claim is equally strong either way — what changes is
      // whether it presents itself as a fact that may not be one yet.
      return unsettled
        ? 'Treat this call as storing audio and video.'
        : 'Audio and video are both being stored.'
  }
}

/**
 * `leadCarriesGuidance` drops the "treat this as recording" tail, because the
 * headline has already said it.
 *
 * This whole sentence is one `aria-live` announcement. Before the guidance
 * headline existed, each qualifier had to carry its own instruction — nothing
 * ahead of it did. Now the `both`-unsettled lead says "Treat this call as
 * storing audio and video", and leaving the tails in produces "…treat this as
 * recording … treat this as recording" in a single spoken string, which is how
 * a screen-reader user learns to talk over the region rather than listen to it.
 *
 * `stopping` keeps its tail either way: "it may still be recording" is a
 * different and sharper fact than the lead's guidance, not a restatement.
 */
function audioQualifier(
  presentation: RecordingPresentation,
  leadCarriesGuidance = false,
): string | null {
  switch (presentation) {
    case 'starting':
      return leadCarriesGuidance
        ? 'Audio storage is starting.'
        : 'Audio storage is starting — treat this as recording.'
    case 'stopping':
      return 'Audio storage is stopping — it may still be recording.'
    case 'failed':
      return leadCarriesGuidance
        ? 'Audio storage state is unresolved.'
        : 'Audio storage state is unresolved — treat this as recording.'
    // No qualifier: `headline` already carries the whole armed clause, and this
    // sentence is read aloud by the live region on every change. Saying it
    // twice in one announcement is how a screen-reader user learns to talk over
    // it.
    case 'armed':
      return null
    default:
      return null
  }
}

function videoQualifier(
  presentation: VideoPresentation,
  leadCarriesGuidance = false,
): string | null {
  switch (presentation) {
    case 'starting':
      return leadCarriesGuidance
        ? 'Video storage is starting.'
        : 'Video storage is starting — treat this as recording.'
    case 'stopping':
      return 'Video storage is stopping — it may still be recording.'
    case 'reconfiguring':
      // The dangerous one, and the reason it is a state rather than a flag:
      // video does not pause while the selection moves. `videoSubjectSentence`
      // names the two selections; this only has to say that neither of them is
      // a gap in the recording.
      return 'The selection is changing, and video keeps being stored while it does.'
    case 'failed':
      return 'Video storage state is unresolved — treat this as recording.'
    default:
      return null
  }
}

/**
 * One cadence for a screen that shows both channels.
 *
 * The MINIMUM, not the average: the two channels converge independently and in
 * either direction, so a fast-moving video reconfigure must not be sampled at
 * audio's settled baseline just because audio happens to be idle. Each channel
 * keeps its own diverged-window clock, because "asked for video, nobody has a
 * camera on" and "asked for audio, nobody has unmuted" start at different
 * moments and expire at different moments.
 */
export function retentionPollInterval(
  audio: RoomAudioRecordingPolicy | undefined,
  video: RoomVideoRecordingPolicy | null | undefined,
  audioDivergedForMs: number,
  videoDivergedForMs: number,
): number {
  return Math.min(
    recordingPollInterval(audio, audioDivergedForMs),
    videoPollInterval(video, videoDivergedForMs),
  )
}

/* -------------------------------------------------------------------------- */
/* what the creator's video control is allowed to be                           */
/* -------------------------------------------------------------------------- */

/**
 * The same seven-branch shape as `recordingControlState`, with one real
 * difference: `ready` carries the CURRENT spec rather than a `next` value.
 *
 * Audio is a toggle, so "what the button would do" is a single derivable
 * value. Video is a choice among five modes plus a focus identity, so there is
 * no single next state to precompute — the control is a picker, and the state
 * machine's job is only to say whether it may be used and what it is showing.
 */
export type VideoControlState =
  | { kind: 'observer' }
  | { kind: 'checking' }
  | { kind: 'unavailable'; reason: string }
  | { kind: 'busy'; target: VideoRecordingSpec }
  | { kind: 'converging'; target: VideoRecordingSpec }
  | { kind: 'failed'; error: string; redrive: VideoRecordingSpec }
  | { kind: 'ready'; current: VideoRecordingSpec }

export interface VideoControlInputs {
  policy: RoomVideoRecordingPolicy
  canControl: boolean
  /**
   * Sentence from whatever the deployment answers when live video policy
   * changes are not supported.
   *
   * Unlike audio, upstream documents NO error code for this: the PUT's 409 is
   * described only as "Revision conflict or the room is already closed", and
   * nothing in the policy snapshot says whether the endpoint is usable. So the
   * caller discovers it the same way it discovers audio's — by attempting a
   * write — and hands the sentence back here.
   */
  unavailableReason: string | null
  probing: boolean
  pending: VideoRecordingSpec | null
}

export function videoControlState({
  policy,
  canControl,
  unavailableReason,
  probing,
  pending,
}: VideoControlInputs): VideoControlState {
  if (!canControl) return { kind: 'observer' }
  if (unavailableReason) return { kind: 'unavailable', reason: unavailableReason }
  if (pending) return { kind: 'busy', target: pending }
  if (videoConverging(policy)) return { kind: 'converging', target: policy.desired }
  if (policy.transition === 'failed') {
    return {
      kind: 'failed',
      error: policy.error ?? 'The recorder could not apply the last video change.',
      // Re-sends the SAME desired spec, never a different one, for the same
      // reason as audio: an idempotent re-drive of a terminal revision, offered
      // as a button and never performed on a timer.
      redrive: policy.desired,
    }
  }
  if (probing) return { kind: 'checking' }
  return { kind: 'ready', current: policy.desired }
}

export function videoActionLabel(state: VideoControlState): string {
  switch (state.kind) {
    case 'busy':
      return state.target.mode === 'none' ? 'Asking to stop…' : 'Asking to change…'
    case 'converging':
      return state.target.mode === 'none' ? 'Recorder is stopping…' : 'Recorder is changing…'
    case 'failed':
      return 'Try again'
    case 'checking':
      return 'Checking…'
    case 'ready':
      return videoModeLabel(state.current.mode)
    default:
      return ''
  }
}

/**
 * The one validation upstream will 422 on, checked before the request.
 *
 * `focus_identity` is REQUIRED for `focused_guest` and optional for `auto`.
 * The distinction is not arbitrary: `focused_guest` is the only mode that makes
 * a negative privacy claim about everybody else, and it cannot make it without
 * naming somebody.
 */
export function videoSpecError(spec: VideoRecordingSpec): string | null {
  if (spec.mode === 'focused_guest' && !spec.focus_identity) {
    return 'Choose which participant’s camera to keep. The recorder cannot infer it from join order.'
  }
  return null
}
